import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { bumexFeatureSelectors } from '@panel/core/src/state/ducks/bumexFeatures';
import { bumexFeaturesKeys } from '@panel/core/src/constants/bumexFeatures';
import { selectColor } from '../../core';
import Checkbox from '../Input/InputCheckbox';
import { getColor } from '../../core/themev3';
import Atencion from '../../assets/img/atencion.svg';
import FinishReason from './FinishReason';
import { sendCustomTrackingInfo } from '../../index';

const EndModal = ({
  postingId,
  onClose,
  device,
  particularNotFree,
  publisherTypeId,
  bulkIds,
  finishReason,
  hasDuplicate,
  SuspendOperations,
  UnreadLeadsMsgOperations,
  hasUnreadLeads
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isProfessional = publisherTypeId !== '1';
  const [step, setStep] = useState(1);
  const [checkConfirmation, setCheckConfirmation] = useState(false);
  const [finishReasonSelected, setFinishReasonSelected] = useState({});
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [finishReasonText, setFinishReasonText] = useState(null);
  const { bumexFeatures } = useSelector(bumexFeatureSelectors.getBumexFeatureState);
  // verificamos que en caso de error del back si es profesional que muestre el modal anterior
  const hasFinishReason = finishReason && finishReason.length;
  const publisherType = isProfessional ? 'profesionales' : 'particulares';
  const isOptionWithDescription =
    finishReasonSelected.motivo === 'other' ||
    finishReasonSelected.motivo === 'fraud' ||
    finishReasonSelected.motivo === 'infringement';

  const reasonsToShowUnreadLeadsModal = ['sell_rent', 'operation_cancelled', 'other'];
  const enabledUnreadLeadsModal =
    bumexFeatures.get(bumexFeaturesKeys.ENABLED_CHECK_UNREAD_LEADS) &&
    reasonsToShowUnreadLeadsModal.includes(finishReasonSelected.motivo) &&
    hasUnreadLeads;

  const onChangeText = e => setFinishReasonText(e.target.value);

  const dispatchSuspend = () => {
    dispatch(
      SuspendOperations.suspendPosting(
        bulkIds || [postingId],
        particularNotFree,
        finishReasonSelected.id,
        isOptionWithDescription ? finishReasonText : null
      )
    );
    onClose();
    sendCustomTrackingInfo({
      event: 'trackEvent',
      eventCategory: 'Panel',
      eventAction: 'Mis avisos',
      eventLabel: 'Finalizar Aviso-finalizar'
    });
    if (enabledUnreadLeadsModal) {
      dispatch(UnreadLeadsMsgOperations.setShowUnreadLeadsModal(true, bulkIds || [postingId]));
    }
  };

  const handleOnClick = () => (!isProfessional ? setStep(2) : dispatchSuspend());

  useEffect(() => {
    if (isOptionWithDescription && finishReasonText) {
      // se valida en el caso de motivo con descripcion el min y max de caracteres
      return setButtonDisabled(finishReasonText.length < 3 || finishReasonText.length > 256);
    }
    return setButtonDisabled(!finishReasonSelected.motivo || isOptionWithDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishReasonSelected, finishReasonText]);

  return (isProfessional && hasFinishReason) || step === 1 ? (
    <FinishReason
      hasDuplicate={hasDuplicate}
      bulkIds={bulkIds}
      device={device}
      finishReason={finishReason}
      handleOnClick={handleOnClick}
      buttonDisabled={buttonDisabled}
      finishReasonSelected={finishReasonSelected}
      setFinishReasonSelected={setFinishReasonSelected}
      onChangeText={onChangeText}
      publisherType={publisherType}
      isOptionWithDescription={isOptionWithDescription}
      ctaText={!isProfessional ? 'Continuar' : null}
      isProfessional={isProfessional}
    />
  ) : (
    <Container device={device} data-panel='panel-modal-endPost-container' isSecondStep={step === 2}>
      <Img src={Atencion} data-panel='panel-modal-endPost-image' isSecondStep={step === 2} />
      <div>
        <h3 data-panel='panel-modal-endPost-title'>
          {t(`panel.avisos.finish.modal.title${bulkIds && bulkIds.length > 1 ? '.plural' : ''}`, {
            value: bulkIds && bulkIds.length
          })}
        </h3>
        <p data-panel='panel-modal-endPost-text'>
          {t(
            `panel.avisos.finish.modal.text${particularNotFree || publisherTypeId !== '1' ? '.particularNotFree' : ''}${
              bulkIds && bulkIds.length > 1 ? '.plural' : ''
            }`
          )}
        </p>
        {particularNotFree && (
          <ParticularCheckContainer
            onClick={() => setCheckConfirmation(!checkConfirmation)}
            data-panel='panel-modal-endPost-particularCheck'
          >
            <Checkbox
              onChange={() => setCheckConfirmation(!checkConfirmation)}
              name='finish-particular-confirmation'
              checked={checkConfirmation}
              fillColor={getColor('primary')}
            />
            <span>{t('panel.avisos.finish.modal.check')}</span>
          </ParticularCheckContainer>
        )}
      </div>
      <CTA
        disabled={particularNotFree && !checkConfirmation}
        onClick={dispatchSuspend}
        data-panel='panel-modal-endPost-buttonCTA'
      >
        {t(`panel.avisos.finish.modal.CTA${bulkIds && bulkIds.length > 1 ? '.plural' : ''}`)}
      </CTA>
    </Container>
  );
};

const Container = styled.div`
  padding: ${({ device }) => (device === 'mobile' ? '20px' : '40px')};
  box-sizing: border-box;
  height: ${({ device }) => (device === 'mobile' ? 'fit-content' : '100%')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  max-width: ${({ isSecondStep }) => (isSecondStep ? 507 : 400)}px;
  height: ${({ isSecondStep }) => (isSecondStep ? 536 : 436)}px;
  h3 {
    font-family: Hind;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 26px;
    letter-spacing: 0em;
    text-align: center;
    white-space: normal;
    margin-bottom: 8px;
  }
  p {
    font-family: Hind;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 26px;
    letter-spacing: 0em;
    text-align: center;
    white-space: normal;
    color: ${selectColor('grayDark')};
    margin-bottom: 32px;
  }
`;

const Img = styled.div`
  background-image: url(${({ src }) => src});
  width: 145px;
  height: 96px;
  ${({ isSecondStep }) =>
    isSecondStep
      ? css`
          margin-top: 40px;
        `
      : css`
          margin-bottom: 32px;
        `}
`;

const CTA = styled.button`
  height: 40px;
  width: 176px;
  border-radius: 8px;
  padding: 0px 12px;
  color: white;
  background-color: ${getColor('primary')};
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: center;
  cursor: pointer;
  ${({ disabled }) =>
    disabled
      ? css`
          background-color: ${selectColor('grayLight')};
          color: ${selectColor('grayMediumLight')};
          cursor: auto;
        `
      : css`
          :hover {
            background-color: ${getColor('secondary')};
          }
        `};
`;

const ParticularCheckContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 45px;
  cursor: pointer;
  > div {
    display: inline-flex;
  }
`;

export default EndModal;
